import { bearingDeg, compassPoint, formatDistance, haversineM } from "../map/geo";
import { altitudeColor } from "../map/icons";
import type { Aircraft, AircraftDetail, AircraftPhoto, FlightRoute, Outlook, ProjectionPoint, RouteAirport } from "../map/types";
import { t } from "../i18n/en";
import { formatAltitude, formatSpeed, type Units } from "../units";

const num = (v: number) => v.toLocaleString("en-GB", { maximumFractionDigits: 0 });

function airportCode(a: RouteAirport): string {
  return a.iata ?? a.icao ?? "?";
}

function formatEta(minutes: number): string {
  const arrival = new Date(Date.now() + minutes * 60_000);
  const clock = arrival.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const span = minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes} min`;
  return `${t.detail.etaPrefix} ${clock} (${span})`;
}

/**
 * The panel for a selected aircraft — deliberately compact.
 *
 * It used to lead with a full-width photo banner and a labelled three-column grid, which on a
 * phone took half the screen and on a desktop covered the entire map. The map is the thing being
 * looked at; this is a caption for one marker on it. So: a thumbnail beside the title, the route
 * on one line, the numbers on one line, and the actions on one line.
 *
 * Model still leads (PLAN D1).
 */
export default function DetailSheet({
  aircraft,
  detail,
  photo,
  route,
  outlook,
  projection,
  projectionLoading,
  devicePosition,
  homePosition,
  units,
  isMuted,
  onToggleMute,
  onClose,
}: {
  aircraft: Aircraft;
  detail: AircraftDetail | null;
  photo: AircraftPhoto | null;
  route: FlightRoute | null;
  outlook: Outlook[];
  projection: ProjectionPoint[];
  projectionLoading: boolean;
  devicePosition: { lat: number; lon: number } | null;
  /** Fallback reference when device location is unavailable (denied, or not yet found). */
  homePosition: { lat: number; lon: number } | null;
  units: Units;
  isMuted: boolean;
  onToggleMute: ((icaoType: string) => void) | null;
  onClose: () => void;
}) {
  const model = aircraft.model ?? detail?.model ?? undefined;
  const remembered = !aircraft.model && detail?.model_source === "remembered";
  const color = altitudeColor(aircraft.alt, aircraft.ground);

  // From the device when we know where it is; otherwise from the saved home point, labelled as such.
  // Without the fallback, anyone who declined location access saw no distance at all.
  const reference = devicePosition ?? homePosition;
  const fromHome = !devicePosition && Boolean(homePosition);
  const distance = reference ? haversineM(reference.lat, reference.lon, aircraft.lat, aircraft.lon) : null;
  const bearing = reference ? bearingDeg(reference.lat, reference.lon, aircraft.lat, aircraft.lon) : null;

  const stats: string[] = [];
  if (distance !== null)
    stats.push(
      `${formatDistance(distance)}${bearing !== null ? ` ${compassPoint(bearing)}` : ""}${fromHome ? ` ${t.detail.fromHome}` : ""}`,
    );
  if (aircraft.ground) stats.push(t.detail.onGround);
  else if (aircraft.alt !== undefined) {
    const trend = aircraft.vs === undefined || Math.abs(aircraft.vs) < 100 ? "" : aircraft.vs > 0 ? " ▲" : " ▼";
    stats.push(`${formatAltitude(aircraft.alt, units)}${trend}`);
  }
  if (aircraft.gs !== undefined) stats.push(formatSpeed(aircraft.gs, units));
  if (aircraft.track !== undefined) stats.push(`${Math.round(aircraft.track)}°`);
  if (aircraft.squawk) stats.push(`${t.detail.squawk} ${aircraft.squawk}`);

  const forecast = projectionLoading
    ? null
    : projection.length > 1
      ? t.detail.forecastShort(Math.round(projection[projection.length - 1].t / 60))
      : t.detail.noForecastShort;

  return (
    <div className="sheet">
      <div className="sheet-head">
        {/* Credit and link-back are Planespotters' conditions of use; the thumbnail is the link. */}
        {photo && (
          <a
            className="sheet-thumb"
            href={photo.link}
            target="_blank"
            rel="noreferrer noopener"
            title={photo.photographer ? `© ${photo.photographer} · Planespotters` : "Planespotters"}
          >
            <img src={photo.url} alt={aircraft.reg ?? t.detail.photoAlt} loading="lazy" />
          </a>
        )}
        <div className="sheet-titles">
          <div className="sheet-model" style={{ color }}>
            {model ?? aircraft.type ?? t.detail.unknownType}
            {remembered && <span className="badge" title={t.detail.rememberedHint}>{t.detail.remembered}</span>}
            {/* The size tier alert filters use — shown so the tiers are learnable. */}
            {detail?.size && t.detail.sizeLabel[detail.size] && <span className="badge">{t.detail.sizeLabel[detail.size]}</span>}
          </div>
          <div className="sheet-sub">
            {[aircraft.type, aircraft.reg, aircraft.flight, route?.airline].filter(Boolean).join(" · ") || aircraft.hex}
          </div>
        </div>
        <button className="icon-button" onClick={onClose} aria-label={t.detail.close}>
          ✕
        </button>
      </div>

      {route && (
        <div className={`sheet-route${route.verified ? "" : " unverified"}`}>
          <span className="route-leg" title={[route.origin.name, route.origin.country].filter(Boolean).join(", ")}>
            <strong>{airportCode(route.origin)}</strong> {route.origin.city}
          </span>
          <span className="route-arrow">→</span>
          <span className="route-leg" title={[route.destination.name, route.destination.country].filter(Boolean).join(", ")}>
            <strong>{airportCode(route.destination)}</strong> {route.destination.city}
          </span>
          <span className="route-meta">
            {route.verified
              ? route.eta_minutes !== null
                ? formatEta(route.eta_minutes)
                : `${num(route.remaining_km)} km ${t.detail.toGo}`
              : t.detail.routeUnverified}
          </span>
        </div>
      )}

      <div className="sheet-stats">{stats.join("  ·  ")}</div>

      {/* "Will this alert me?" answered for the real aircraft, by the engine's own logic. */}
      {outlook.length > 0 && (
        <div className={`sheet-outlook${outlook[0].blocked_by ? " blocked" : ""}`}>
          {t.detail.outlook(outlook[0].rule, outlook[0].entry_s, outlook[0].blocked_by)}
          {outlook.length > 1 && <span className="dim"> {t.detail.outlookMore(outlook.length - 1)}</span>}
        </div>
      )}

      {aircraft.emg && (
        <p className="emergency">
          {t.detail.emergency}: {aircraft.emg}
        </p>
      )}

      <div className="sheet-actions">
        {aircraft.type && onToggleMute && (
          <button className={`compact${isMuted ? "" : " secondary"}`} onClick={() => onToggleMute(aircraft.type!)}>
            {isMuted ? t.detail.unmuteType(aircraft.type) : t.detail.muteType(aircraft.type)}
          </button>
        )}
        {forecast && <span className="sheet-note">{isMuted ? `${t.detail.mutedShort} · ` : ""}{forecast}</span>}
      </div>
    </div>
  );
}
